import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, BookOpen, Orbit, Sparkles, FileText, Compass, Moon, Library } from "lucide-react";
import { toast } from "sonner";
import { getRandom } from "@/lib/api";

const GOLD = "#D8B66A";
const GOLD_INK = "#0A1526";

const STEPS = [
  { key: "welcome", eyebrow: "Welcome to the Archive",
    lead: "Hundreds of files. Thousands of connections. One question leads to another.",
    body: "CODEX is built to help you explore the stories, mysteries, people, events, and ideas hiding beneath the surface.",
    cta: "Begin Tour", art: "welcome" },
  { key: "archive", eyebrow: "The Archive",
    lead: "Every mystery begins with a file.",
    body: "Browse hundreds of topics, follow related evidence, open sources, and keep going deeper.",
    cta: "Continue", art: "archive" },
  { key: "universe", eyebrow: "The Universe",
    lead: "Nothing exists in isolation.",
    body: "The Universe reveals the hidden relationships between CODEX files — letting you visually follow connections across the Archive.",
    cta: "Continue", art: "universe" },
  { key: "path", eyebrow: "Choose your path",
    lead: "Where do you want to begin?", choose: true, art: "path" },
];

// ---- deterministic celestial ambiance shared by every step ----
const STARS = Array.from({ length: 46 }, (_, i) => {
  const r = (s) => { const x = Math.sin((i + 1) * s) * 10000; return x - Math.floor(x); };
  return { x: r(12.9), y: r(78.2), s: 0.5 + r(3.7) * 1.6, o: 0.18 + r(5.1) * 0.5, d: r(9.3) * 4 };
});

function StarMap() {
  return (
    <svg className="ct-art" viewBox="0 0 900 620" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {/* faint celestial-map arcs */}
      <g stroke={GOLD} strokeOpacity="0.06" fill="none" strokeWidth="1">
        <circle cx="120" cy="120" r="260" /><circle cx="820" cy="520" r="300" />
        <ellipse cx="450" cy="310" rx="420" ry="230" />
      </g>
      {/* constellation lines */}
      <g stroke={GOLD} strokeOpacity="0.14" strokeWidth="0.8" fill="none">
        <polyline points="80,120 160,180 210,140 300,210" />
        <polyline points="640,90 720,140 690,220 780,260" />
      </g>
      {STARS.map((st, i) => (
        <circle key={i} className="ct-twinkle" cx={st.x * 900} cy={st.y * 620} r={st.s}
          fill={i % 5 === 0 ? "#FBF3DA" : GOLD} opacity={st.o} style={{ animationDelay: `${st.d}s` }} />
      ))}
      {/* archival coordinate markings */}
      <g fill={GOLD} fillOpacity="0.16" fontFamily="monospace" fontSize="9" letterSpacing="1">
        <text x="40" y="560">41°17′ N</text><text x="770" y="70">88°02′ W</text>
        <text x="60" y="90">VERITAS</text>
      </g>
    </svg>
  );
}

function CompassEmblem() {
  return (
    <svg width="34" height="34" viewBox="0 0 40 40" aria-hidden="true" className="mx-auto opacity-90">
      <g stroke={GOLD} strokeWidth="1.1" fill="none">
        <circle cx="20" cy="20" r="6.5" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const rad = (a * Math.PI) / 180, r1 = a % 90 === 0 ? 8 : 7.5, r2 = a % 90 === 0 ? 18 : 12;
          return <line key={a} x1={20 + Math.cos(rad) * r1} y1={20 + Math.sin(rad) * r1}
            x2={20 + Math.cos(rad) * r2} y2={20 + Math.sin(rad) * r2} />;
        })}
      </g>
      <circle cx="20" cy="20" r="2" fill={GOLD} />
    </svg>
  );
}

const Divider = () => (
  <div className="my-4 flex items-center justify-center gap-3" aria-hidden="true">
    <span className="h-px w-14 bg-gradient-to-r from-transparent to-gold-soft/40" />
    <span className="text-gold-soft/70 text-xs">✦</span>
    <span className="h-px w-14 bg-gradient-to-l from-transparent to-gold-soft/40" />
  </div>
);

// ---- per-step background art (all pointer-events:none, behind content) ----
function ArchiveArt() {
  const card = "fill-none stroke-gold-soft";
  return (
    <svg className="ct-art" viewBox="0 0 900 620" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g strokeWidth="1" opacity="0.5">
        <g transform="translate(70 360) rotate(-6)" className={card} strokeOpacity="0.28">
          <rect x="0" y="0" width="150" height="200" rx="6" />
          <line x1="18" y1="150" x2="120" y2="150" strokeOpacity="0.18" />
        </g>
        <g transform="translate(700 340) rotate(7)" className={card} strokeOpacity="0.26">
          <rect x="0" y="0" width="150" height="210" rx="6" />
          <rect x="18" y="24" width="34" height="34" rx="4" strokeOpacity="0.3" />
        </g>
      </g>
      <g fill={GOLD} fillOpacity="0.2" fontFamily="monospace" fontSize="11" letterSpacing="1">
        <text x="86" y="430">CASE FILE</text><text x="86" y="448" fillOpacity="0.13">No. 04217</text>
        <text x="720" y="418">EVIDENCE</text><text x="720" y="436" fillOpacity="0.13">No. 771-A</text>
        <text x="720" y="500">SOURCE</text><text x="720" y="518" fillOpacity="0.13">Open Archive</text>
      </g>
    </svg>
  );
}

function ConnectionMap({ animate }) {
  const nodes = [
    { x: 150, y: 120, Icon: FileText }, { x: 300, y: 70, Icon: Compass },
    { x: 640, y: 90, Icon: Moon }, { x: 780, y: 150, Icon: BookOpen },
    { x: 210, y: 250, Icon: Compass }, { x: 700, y: 250, Icon: Library },
  ];
  const cx = 450, cy = 170;
  return (
    <svg className="ct-art ct-art--front" viewBox="0 0 900 340" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <g stroke={GOLD} strokeOpacity="0.35" fill="none" strokeWidth="1">
        {nodes.map((n, i) => (
          <path key={i} className={animate ? "ct-draw" : ""}
            d={`M ${cx} ${cy} Q ${(cx + n.x) / 2} ${(cy + n.y) / 2 - 30} ${n.x} ${n.y}`}
            style={{ animationDelay: `${0.15 * i}s` }} />
        ))}
      </g>
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r="19" fill="#0C1A30" stroke={GOLD} strokeOpacity="0.5" />
          <foreignObject x={n.x - 9} y={n.y - 9} width="18" height="18">
            <n.Icon size={18} color={GOLD} strokeWidth={1.5} />
          </foreignObject>
        </g>
      ))}
      {/* bright central node */}
      <g className="ct-pulse">
        <circle cx={cx} cy={cy} r="30" fill={GOLD} opacity="0.10" />
        <circle cx={cx} cy={cy} r="15" fill={GOLD} opacity="0.18" />
        <circle cx={cx} cy={cy} r="5.5" fill="#FBF3DA" />
        {[0, 60, 120, 180, 240, 300].map((a) => {
          const r = (a * Math.PI) / 180;
          return <line key={a} x1={cx} y1={cy} x2={cx + Math.cos(r) * 26} y2={cy + Math.sin(r) * 26}
            stroke={GOLD} strokeOpacity="0.5" strokeWidth="1" />;
        })}
      </g>
    </svg>
  );
}

function OrbitArt() {
  return (
    <svg className="ct-art" viewBox="0 0 900 620" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <g stroke={GOLD} strokeOpacity="0.16" fill="none" strokeWidth="1">
        <ellipse cx="150" cy="360" rx="150" ry="230" />
        <ellipse cx="150" cy="360" rx="230" ry="150" transform="rotate(24 150 360)" />
        <ellipse cx="150" cy="360" rx="110" ry="290" transform="rotate(-18 150 360)" />
      </g>
      <circle cx="150" cy="360" r="6" fill="#FBF3DA" opacity="0.9" />
      <circle cx="150" cy="360" r="16" fill={GOLD} opacity="0.12" />
      <g fill={GOLD}>
        <circle cx="815" cy="250" r="16" opacity="0.28" />
        <circle cx="835" cy="470" r="10" opacity="0.22" />
      </g>
    </svg>
  );
}

const PATHS = [
  { key: "archive", Icon: BookOpen, title: "Browse the Archive", desc: "Open case files and follow the evidence", testid: "home-tour-path-archive", to: "/explore", reason: "archive" },
  { key: "universe", Icon: Orbit, title: "Enter the Universe", desc: "See how every file connects", testid: "home-tour-path-universe", to: "/universe?tour=invite", reason: "universe" },
  { key: "surprise", Icon: Sparkles, title: "Surprise Me", desc: "Open a random mystery", testid: "home-tour-path-surprise", surprise: true },
];

export function HomeTour({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  useEffect(() => { if (open) setStep(0); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose && onClose("skip");
      if (e.key === "ArrowRight" && step < STEPS.length - 1) setStep((n) => n + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep((n) => n - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, step]);

  if (!open) return null;
  const s = STEPS[step];
  const go = (path, reason) => { onClose && onClose(reason); navigate(path); };
  const surprise = async () => {
    try { const r = await getRandom(); onClose && onClose("surprise"); navigate(`/topic/${r.slug}`); }
    catch (e) { toast("Couldn't open a random Case File — please try again."); }
  };

  return (
    <div
      data-testid="home-tour-overlay"
      role="dialog" aria-modal="true" aria-label="CODEX guided tour"
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-[#040912]/85 backdrop-blur-md"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose("skip"); }}
    >
      <TourStyles />
      <div className="relative flex max-h-[94vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[26px] border border-gold-soft/30 bg-[#0A1526]/92 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)]">
        {/* inner gold hairline + ambient art */}
        <div className="pointer-events-none absolute inset-[6px] rounded-[20px] border border-gold-soft/12" />
        <StarMap />
        {s.art === "archive" && <ArchiveArt />}
        {s.art === "path" && <OrbitArt />}
        {/* soft haze */}
        <div className="pointer-events-none absolute -bottom-10 -left-10 h-56 w-72 rounded-full opacity-50" style={{ background: "radial-gradient(closest-side, rgba(60,80,130,0.5), transparent)", filter: "blur(24px)" }} />
        <div className="pointer-events-none absolute -bottom-8 right-0 h-52 w-72 rounded-full opacity-40" style={{ background: "radial-gradient(closest-side, rgba(50,66,110,0.5), transparent)", filter: "blur(24px)" }} />
        <div className="ct-grain pointer-events-none absolute inset-0" />

        {/* content */}
        <div className="relative flex flex-1 flex-col overflow-y-auto px-6 py-8 text-center sm:px-12 sm:py-11">
          <button
            data-testid="home-tour-skip"
            onClick={() => onClose && onClose("skip")}
            className="absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] uppercase tracking-[0.22em] text-parchment/60 transition-colors hover:text-gold-light"
          >
            Skip <X className="h-3.5 w-3.5" />
          </button>

          <div className="mt-1"><CompassEmblem /></div>

          <div key={s.key} className="ct-step flex flex-1 flex-col">
            <p className="mt-4 text-[11px] uppercase tracking-[0.32em] text-gold-soft/85 sm:text-xs">{s.eyebrow}</p>
            <Divider />

            <h2 className="ct-head mx-auto max-w-[15ch] font-wordmark leading-[1.08] text-parchment"
              style={{ fontVariant: "small-caps", letterSpacing: "0.01em" }}>
              {s.lead}
            </h2>

            {s.body ? (
              <>
                <Divider />
                <p className="mx-auto max-w-[46ch] text-[15px] leading-7 text-parchment/80 sm:text-base">{s.body}</p>
              </>
            ) : null}

            {s.art === "universe" ? (
              <div className="relative mx-auto mt-5 w-full max-w-[640px]">
                <ConnectionMap animate />
              </div>
            ) : null}

            {s.choose ? (
              <div className="relative mx-auto mt-7 flex w-full max-w-[620px] flex-col gap-3">
                {PATHS.map((p) => (
                  <button
                    key={p.key}
                    data-testid={p.testid}
                    onClick={() => (p.surprise ? surprise() : go(p.to, p.reason))}
                    className="ct-option group relative flex items-center gap-4 rounded-2xl border border-gold-soft/25 bg-[#0C1A30]/70 px-5 py-4 text-left transition-all duration-200 hover:border-gold-light/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-light/70"
                  >
                    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl border border-gold-soft/40 bg-gold-light/10 text-gold-light">
                      <p.Icon className="h-5 w-5" strokeWidth={1.6} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-serif text-lg text-parchment">{p.title}</span>
                      <span className="block text-xs text-parchment/55">{p.desc}</span>
                    </span>
                    <ArrowRight className="h-5 w-5 flex-none text-gold-soft transition-transform duration-200 group-hover:translate-x-1 group-hover:text-gold-light" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-8 flex justify-center">
                <button
                  data-testid="home-tour-next"
                  onClick={() => setStep((n) => Math.min(n + 1, STEPS.length - 1))}
                  className="ct-cta group inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 font-serif text-[17px] font-semibold"
                  style={{ color: GOLD_INK }}
                >
                  {s.cta}
                  <ArrowRight className="h-[18px] w-[18px] transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </div>

          {/* progress */}
          <div className="mt-8">
            <Divider />
            <div className="flex items-center justify-center gap-2.5">
              {STEPS.map((st, i) => (
                <button
                  key={st.key}
                  aria-label={`Go to step ${i + 1}`}
                  onClick={() => setStep(i)}
                  className={`h-[6px] rounded-full transition-all duration-500 ${
                    i === step ? "w-7 bg-gold-light shadow-[0_0_10px_rgba(216,182,106,0.7)]"
                    : i < step ? "w-[6px] bg-gold-soft/55" : "w-[6px] bg-white/15 hover:bg-white/30"}`}
                />
              ))}
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-parchment/45">Step {step + 1} of {STEPS.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TourStyles() {
  return (
    <style>{`
      .ct-art{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;}
      .ct-art--front{position:relative;inset:auto;height:auto;}
      .ct-grain{opacity:.05;mix-blend-mode:overlay;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");}
      .ct-head{font-size:clamp(1.9rem,4.4vw,3.1rem);}
      .ct-cta{background:linear-gradient(180deg,#F1D98A 0%,#D8B66A 55%,#BE9A45 100%);box-shadow:0 0 22px rgba(216,182,106,.35),inset 0 1px 0 rgba(255,255,255,.4);transition:transform .18s ease,box-shadow .18s ease,filter .18s ease;}
      .ct-cta:hover{filter:brightness(1.06);transform:translateY(-1px);box-shadow:0 6px 26px rgba(216,182,106,.5),inset 0 1px 0 rgba(255,255,255,.5);}
      .ct-cta:focus-visible{outline:2px solid rgba(216,182,106,.9);outline-offset:3px;}
      .ct-option:hover{box-shadow:0 0 24px -6px rgba(216,182,106,.45);transform:translateY(-1px);}
      .ct-step{animation:ctIn .5s cubic-bezier(.22,.7,.24,1) both;}
      @keyframes ctIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
      .ct-twinkle{animation:ctTw 4.5s ease-in-out infinite;}
      @keyframes ctTw{0%,100%{opacity:.2}50%{opacity:.85}}
      .ct-pulse{animation:ctPulse 3.4s ease-in-out infinite;transform-origin:center;}
      @keyframes ctPulse{0%,100%{opacity:.85}50%{opacity:1}}
      .ct-draw{stroke-dasharray:320;stroke-dashoffset:320;animation:ctDraw 1.1s ease forwards;}
      @keyframes ctDraw{to{stroke-dashoffset:0}}
      @media (prefers-reduced-motion: reduce){
        .ct-step,.ct-twinkle,.ct-pulse,.ct-draw{animation:none!important}
        .ct-draw{stroke-dashoffset:0}
        .ct-cta,.ct-option{transition:none}
      }
    `}</style>
  );
}

export default HomeTour;
